import { Api } from '@/core/api.js'
import Vue from 'vue'

const api = new Api()

const getInitialHomeWidgetData = () => ({
    pending: 0
})

let pendingBatchesRequestSeq = 0

const PLS_TRACKED_SOURCES = ['user', 'auto']

const hasExistingForecast = (forecast) => {
    if (!forecast || forecast === false || forecast.defrost === false) {
        return false
    }
    return Object.keys(forecast).length > 0
}

/** Horas que hay que esperar entre dos reseteos del PLS. */
export const PLS_RESET_COOLDOWN_HOURS = 4

const state = {
    categories: {},
    products: {},
    configuration: false,
    forecast: {},
    sales: false,
    defrostSales: false,
    fryers: [],
    cookingProducts: [], // Productos cocinando (unificados)
    processedProductIds: [], // IDs de productos procesados localmente
    selectedCookingProducts: [], // Productos cocinando seleccionados
    plkProducts: [], // Productos de PLK (unificados)
    plkStock: [], // Stock de PLK (unificado)
    salesForecastType: 'MANUAL', // Tipo de forecast de ventas
    options: {},
    homeWidgetData: getInitialHomeWidgetData(),
    resetNotice: false,
    plsResetStatus: {
        cooldown_hours: PLS_RESET_COOLDOWN_HOURS,
        last_reset_at: null,
        available_at: null,
        can_reset: true,
        server_now: null,
        loaded: false // Hasta que el servidor responda no sabemos si se puede resetear.
    }
}

const getters = {
    getStates(state, getters, rootState) {
        return state.states
    },
    getCategories: (state, getters, rootState) => {
        return state.categories
    },
    getProducts: (state, getters, rootState) => {
        return state.products
    },
    getSortedCategories: (state, getters, rootState) => {
        return Object.values(state.categories).sort((a, b) => parseInt(a.order) - parseInt(b.order))
    },
    getSelectedCategory: (state, getters, rootState) => {
        for (var i in state.categories) {
            if (state.categories[i].selected) return state.categories[i]
        }
        return false
    },
    getConfiguration: (state, getters, rootState) => {
        return state.configuration
    },
    getResetNotice: (state) => {
        return state.resetNotice
    },
    getPlsResetStatus: (state) => {
        return state.plsResetStatus
    },
    getForecast: (state, getters, rootState) => {
        return state.forecast
    },
    getSales: (state, getters, rootState) => {
        return state.sales
    },
    getDefrostSales: (state, getters, rootState) => {
        return state.defrostSales
    },
    getFryers: (state, getters, rootState) => {
        return state.fryers.length > 0 ? state.fryers : false
    },
    getCookingProducts: (state, getters, rootState) => {
        return state.cookingProducts || []
    },
    getProcessedProductIds: (state, getters, rootState) => {
        return state.processedProductIds || []
    },
    getSelectedCookingProducts: (state, getters, rootState) => {
        return state.selectedCookingProducts || []
    },
    getPlkProducts: (state, getters, rootState) => {
        return state.plkProducts || []
    },
    getPlkStock: (state, getters, rootState) => {
        return state.plkStock || []
    },
    getSalesForecastType: (state, getters, rootState) => {
        return state.salesForecastType
    },
    getOption: (state, getters, rootState) => (keyname) => {
        return state.options && typeof state.options[keyname] !== 'undefined' ? state.options[keyname] : null
    },
    getHomeWidgetData: (state) => {
        return state.homeWidgetData
    }
}

const actions = {
    loadData(context, params) {
        return api.get('pls/products').then(function(response) {
            var products = response.data.products
            var categories = response.data.categories
            context.commit('setProducts', products)
            return context.commit('setCategories', categories)
        })
    },
    loadFryers(context, params) {
        const url = `pls/fryer/config/get`
        return api.get(url).then(function(response) {
            return context.commit('setFryers', response.data)
        })
    },
    editBatch(context, params) {
        const url = `pls/batch/edit/${params.batchId}`
        return api.post(url, params).then(function(response) {
            return response
        })
    },
    addBatch(context, params) {
        const url = `pls/batch/add`
        return api.post(url, params).then(function(response) {
            return response
        })
    },
    toggleFryer(context, params) {
        const url = `pls/fryer/config/update`
        const data = JSON.stringify(params)
        return api.post(url, { config: data }).then(function(response) {
            return response
        })
    },
    initFryer(context, params) {
        const url = `pls/fryer/init`
        return api.get(url).then(function(response) {
            return response
        })
    },
    loadForecast(context, params) {
        const isObject = params && typeof params === 'object'
        const type = isObject ? (params.type || 'pls') : 'pls'
        const date = isObject ? (params.date || params) : params
        const preserveOnFailure = Boolean(isObject && params.preserveOnFailure)
        const url = `${type}/forecast/${date}?t=${Date.now()}`

        // El POST registra el evento de uso de la herramienta PLS, por lo que solo debe usarlo
        // esa pantalla. El resto de vistas que comparten el forecast siguen consultando por GET.
        const source = isObject && type === 'pls' && PLS_TRACKED_SOURCES.includes(params.source) ? params.source : false
        const request = source ? api.post(url, { source }, { timeout: 60000 }) : api.get(url, { timeout: 60000 })

        return request.then(function(response) {
            if (response.status) {
                context.commit('setForecast', response.data)
                return response
            }
            const currentForecast = context.state && context.state.forecast
            if (preserveOnFailure && hasExistingForecast(currentForecast)) {
                return response
            }
            context.commit('setForecast', false)
            return response
        })
    },
    loadSales(context, params) {
        const url = `pls/resume/${params.dateref}/${params.date1}/${params.date2}`
        return api.get(url).then(function(response) {
            return context.commit('setSales', response.data)
        })
    },
    confirm(context, params) {
        const url = `pls/forecast/confirm/${params.ref}`
        return api.post(url, params.params, { timeout: 60000 }).then(function(response) {
            return response
        })
    },
    saveAdvancedSettings(context, params) {
        return api.post(`pls/forecast/advanced/${params.date}`, {
            settings: JSON.stringify(params.settings)
        }, { timeout: 60000 })
    },
    updateConfiguration(context, params) {
        const url = `pls/products/update`
        return api.post(url, params).then(function(response) {
            return response
        })
    },
    resetForecast(context, date) {
        const url = `pls/forecast/reset/${date}`
        return api.post(url, []).then(function(response) {
            return response
        })
    },
    loadDefrostSales(context, params) {
        const url = `pls/resume/${params.dateref}/${params.date1}/${params.date2}`
        return api.get(url).then(function(response) {
            return context.commit('setDefrostSales', response.data)
        })
    },
    exportForecast(context, params) {
        const url = `pls/export`
        return api.post(url, params)
    },
    removeSelectedCookingProducts(context, params) {
        const selectedProducts = context.getters['getSelectedCookingProducts']
        const batchIds = selectedProducts.map(p => p.batch_id)
        const url = `pls/batch/remove`
        return api.post(url, { batch_id: batchIds.join(',') }).then(function(response) {
            context.commit('clearSelectedCookingProducts')
            return response
        })
    },
    startDemo(context, params) {
        const url = `stock-pls/demo/start`
        return api.get(url).then(function(response) {
            return response
        })
    },
    removeRecommendation(context, params) {
        const batchId = params.batchId
        const url = `pls-stock/batch/remove/${batchId}`
        return api.post(url, {}).then(function(response) {
            return response
        })
    },
    loadPendingBatches(context, params = {}) {
        const requestId = ++pendingBatchesRequestSeq

        if (params.reset) {
            context.commit('resetHomeWidgetData')
        }

        const url = 'pls-stock/pending-batches'
        return api.get(url).then(function(response) {
            if (requestId !== pendingBatchesRequestSeq) {
                return response
            }

            if (response.status && response.data) {
                context.commit('setHomeWidgetData', {
                    pending: response.data.count || 0
                })
            }
            return response
        })
    },
    loadPlsResetStatus(context, params = {}) {
        const suffix = params.date ? `/${params.date}` : ''

        return api.get(`pls-stock/reset/status${suffix}`).then(function(response) {
            if (response.status && response.data) {
                context.commit('setPlsResetStatus', response.data)
            }
            return response
        })
    },
    resetPlsChef(context, params) {
        const suffix = params.date ? `/${params.date}` : ''

        return api.post(`pls-stock/reset${suffix}`, {
            stock: JSON.stringify(params.stock)
        }, { timeout: 60000 }).then(function(response) {
            if (response.data) {
                context.commit('setPlsResetStatus', response.data)
            }
            return response
        })
    },
    resetHomeWidgetData(context) {
        pendingBatchesRequestSeq++
        context.commit('resetHomeWidgetData')
    }
}

const mutations = {
    setStates(state, data) {
        state.states = data
    },
    setCategories(state, data) {
        state.categories = data
    },
    setProducts(state, data) {
        state.products = data
    },
    selectCategory(state, category) {
        if (category == false || category == 'false') {
            for (var k in state.categories) {
                var cat = state.categories[k]
                cat.selected = false
            }
        } else {
            for (var k in state.categories) {
                var cat = state.categories[k]
                if (cat.id == category.id) {
                    cat.selected = true
                } else {
                    cat.selected = false
                }
            }
        }
        Vue.set(state, 'categories', { ...state.categories })
    },
    setConfiguration(state, data) {
        state.configuration = data
    },
    setResetNotice(state, value) {
        state.resetNotice = Boolean(value)
    },
    setPlsResetStatus(state, resetStatus) {
        state.plsResetStatus = {
            ...state.plsResetStatus,
            ...resetStatus,
            loaded: true
        }
    },
    toggleProduct(state, id) {
        state.products[id].status == state.products[id].status
    },
    setForecast(state, data) {
        state.forecast = data
    },
    setSales(state, data) {
        state.sales = { ...data }
    },
    setDefrostSales(state, data) {
        state.defrostSales = { ...data }
    },
    setFryers(state, data) {
        state.fryers = data
        Vue.set(state, 'fryers', data)
    },
    // Mutaciones para productos cocinando unificados
    setCookingProducts(state, products) {
        Vue.set(state, 'cookingProducts', products || [])
    },
    addCookingProduct(state, product) {
        if (!state.cookingProducts) {
            state.cookingProducts = []
        }
        // Usar Vue.set para asegurar reactividad
        const newArray = [...state.cookingProducts, product]
        Vue.set(state, 'cookingProducts', newArray)
    },
    removeCookingProduct(state, productId) {
        if (state.cookingProducts) {
            const newArray = state.cookingProducts.filter(p => p.id !== productId)
            Vue.set(state, 'cookingProducts', newArray)
        }
    },
    clearCookingProducts(state) {
        Vue.set(state, 'cookingProducts', [])
    },
    updateCookingProduct(state, { productId, updates }) {
        if (state.cookingProducts) {
            const productIndex = state.cookingProducts.findIndex(p => p.id === productId)
            if (productIndex !== -1) {
                Vue.set(state.cookingProducts, productIndex, { ...state.cookingProducts[productIndex], ...updates })
            }
        }
    },
    // Mutaciones para productos procesados
    addProcessedProductId(state, productId) {
        if (!state.processedProductIds) {
            state.processedProductIds = []
        }
        if (!state.processedProductIds.includes(productId)) {
            // Usar Vue.set para asegurar reactividad
            const newArray = [...state.processedProductIds, productId]
            Vue.set(state, 'processedProductIds', newArray)
        }
    },
    removeProcessedProductId(state, productId) {
        if (state.processedProductIds) {
            const newArray = state.processedProductIds.filter(id => id !== productId)
            Vue.set(state, 'processedProductIds', newArray)
        }
    },
    clearProcessedProductIds(state) {
        Vue.set(state, 'processedProductIds', [])
    },
    // Mutaciones para productos cocinando seleccionados
    addSelectedCookingProduct(state, product) {
        const productExists = state.selectedCookingProducts.some(p => p.batch_id === product.batch_id)
        if (!productExists) {
            const newArray = [...state.selectedCookingProducts, product]
            Vue.set(state, 'selectedCookingProducts', newArray)
        }
    },
    removeSelectedCookingProduct(state, productBatchId) {
        const newArray = state.selectedCookingProducts.filter(p => p.batch_id !== productBatchId)
        Vue.set(state, 'selectedCookingProducts', newArray)
    },
    clearSelectedCookingProducts(state) {
        Vue.set(state, 'selectedCookingProducts', [])
    },
    // Mutaciones para freidoras
    updateFryerStatus(state, { fryerId, status }) {
        if (state.fryers && state.fryers.length > 0) {
            const fryerIndex = state.fryers.findIndex(f => f.id === fryerId)
            if (fryerIndex !== -1) {
                Vue.set(state.fryers, fryerIndex, { ...state.fryers[fryerIndex], status })
            }
        }
    },
    // Mutaciones para productos PLK unificados
    setPlkProducts(state, products) {
        Vue.set(state, 'plkProducts', products || [])
    },
    addPlkProduct(state, product) {
        if (!state.plkProducts) {
            state.plkProducts = []
        }
        // Usar Vue.set para asegurar reactividad
        const newArray = [...state.plkProducts, product]
        Vue.set(state, 'plkProducts', newArray)
    },
    removePlkProduct(state, productId) {
        if (state.plkProducts) {
            const newArray = state.plkProducts.filter(p => p.id !== productId)
            Vue.set(state, 'plkProducts', newArray)
        }
    },
    clearPlkProducts(state) {
        Vue.set(state, 'plkProducts', [])
    },
    updatePlkProduct(state, { productId, updates }) {
        if (state.plkProducts) {
            const productIndex = state.plkProducts.findIndex(p => p.id === productId)
            if (productIndex !== -1) {
                Vue.set(state.plkProducts, productIndex, { ...state.plkProducts[productIndex], ...updates })
            }
        }
    },
    // Mutaciones para stock PLK unificado
    setPlkStock(state, stock) {
        Vue.set(state, 'plkStock', stock || [])
    },
    addPlkStockItem(state, item) {
        if (!state.plkStock) {
            state.plkStock = []
        }
        // Usar Vue.set para asegurar reactividad
        const newArray = [...state.plkStock, item]
        Vue.set(state, 'plkStock', newArray)
    },
    removePlkStockItem(state, itemId) {
        if (state.plkStock) {
            const newArray = state.plkStock.filter(item => item.id !== itemId)
            Vue.set(state, 'plkStock', newArray)
        }
    },
    clearPlkStock(state) {
        Vue.set(state, 'plkStock', [])
    },
    updatePlkStockItem(state, { itemId, updates }) {
        if (state.plkStock) {
            const itemIndex = state.plkStock.findIndex(item => item.id === itemId)
            if (itemIndex !== -1) {
                Vue.set(state.plkStock, itemIndex, { ...state.plkStock[itemIndex], ...updates })
            }
        }
    },
    editBatch(state, { temporalBatchId, originalBatchId }) {
        if (state.plkProducts) {
            const newArray = state.plkProducts.map(p => {
                if(p.batch_id === temporalBatchId){
                    return { ...p, batch_id: originalBatchId }
                }
                return p
            })
            Vue.set(state, 'plkProducts', newArray)
        }
    },
    editCookingBatch(state, { temporalBatchId, originalBatchId }) {
        if (state.cookingProducts) {
            const newArray = state.cookingProducts.map(p => {
                if(p.batch_id === temporalBatchId){
                    return { ...p, batch_id: originalBatchId }
                }
                return p
            })
            Vue.set(state, 'cookingProducts', newArray)
        }
    },
    setSalesForecastType(state, data) {
        Vue.set(state, 'salesForecastType', data)
    },
    setOption(state, { keyname, value }) {
        Vue.set(state, 'options', { ...state.options, [keyname]: value })
    },
    setHomeWidgetData(state, data) {
        Vue.set(state, 'homeWidgetData', { ...state.homeWidgetData, ...data })
    },
    resetHomeWidgetData(state) {
        Vue.set(state, 'homeWidgetData', getInitialHomeWidgetData())
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
